<script>
import {defineComponent} from 'vue'
import {useDeliveryStore} from '@js/store/DeliveryStore.js'

import MobileTopBar from "@js/components/TopBar/MobileTopBar.vue";
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue";

export default defineComponent({
    name: "DeliveryAndShippingPage",
    components: {
        Breadcrumbs,
        MobileTopBar,
    },

    setup() {
        const delivery = useDeliveryStore()
        delivery.setVehicle('pickup')
        return {delivery}
    },

    methods: {
        selectVehicle(vehicleId) {
            this.delivery.setVehicle(vehicleId)
        }
    }
})
</script>

<template>
    <MobileTopBar/>
    <div class="container">
        <Breadcrumbs/>

        <!-- HERO -->
        <section
            class="relative overflow-hidden rounded-3xl border border-gray-200 bg-gradient-to-br from-white via-gray-50 to-gray-100 px-5 py-6 md:px-8 md:py-10 mb-5 md:mb-8">
            <div class="absolute -top-16 -right-16 w-56 h-56 bg-baseColor/10 blur-3xl rounded-full"></div>
            <div class="absolute -bottom-20 -left-10 w-56 h-56 bg-black/5 blur-3xl rounded-full"></div>

            <div class="relative grid grid-cols-1 lg:grid-cols-[1.25fr_.85fr] gap-6 items-center">
                <div>
                    <div
                        class="inline-flex items-center gap-2 rounded-full bg-green-50 text-green-700 border border-green-200 px-3 py-1 text-sm font-medium mb-4">
                        <span class="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                        Бесплатный вывоз металлолома
                    </div>

                    <h1 class="text-2xl md:text-4xl font-semibold leading-tight mb-4">
                        Вывоз металлолома по Москве и области
                    </h1>

                    <p class="text-gray-700 text-sm md:text-base leading-relaxed mb-5 max-w-3xl">
                        Организуем бесплатный вывоз металлолома с объекта, склада, производства, гаража,
                        стройплощадки или частной территории. Подбираем подходящую машину под объём,
                        вес и тип металла: от небольшого самовывоза до контейнера, ломовоза или
                        манипулятора для тяжёлых и габаритных конструкций.
                    </p>

                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div class="rounded-2xl bg-white border border-gray-200 p-4 shadow-sm">
                            <div class="text-sm text-gray-500 mb-1">Стоимость выезда</div>
                            <div class="text-lg font-semibold">Бесплатно</div>
                        </div>

                        <div class="rounded-2xl bg-white border border-gray-200 p-4 shadow-sm">
                            <div class="text-sm text-gray-500 mb-1">Подбор транспорта</div>
                            <div class="text-lg font-semibold">Под ваш объём</div>
                        </div>

                        <div class="rounded-2xl bg-white border border-gray-200 p-4 shadow-sm">
                            <div class="text-sm text-gray-500 mb-1">Что вывозим</div>
                            <div class="text-lg font-semibold">Чёрный и цветной лом</div>
                        </div>
                    </div>
                </div>

                <div class="grid grid-cols-2 gap-3">
                    <div class="rounded-3xl bg-white border border-gray-200 p-4 shadow-sm">
                        <div class="text-xs text-gray-500 mb-2">Малый объём</div>
                        <div class="text-base font-semibold mb-1">Пикап / каблук</div>
                        <div class="text-sm text-gray-600">Кабель, аккумуляторы, цветной металл, небольшие партии</div>
                    </div>

                    <div class="rounded-3xl bg-white border border-gray-200 p-4 shadow-sm">
                        <div class="text-xs text-gray-500 mb-2">Средний объём</div>
                        <div class="text-base font-semibold mb-1">Газель</div>
                        <div class="text-sm text-gray-600">Гаражи, частные адреса, небольшие демонтажи</div>
                    </div>

                    <div class="rounded-3xl bg-white border border-gray-200 p-4 shadow-sm">
                        <div class="text-xs text-gray-500 mb-2">Крупный объём</div>
                        <div class="text-base font-semibold mb-1">Ломовоз</div>
                        <div class="text-sm text-gray-600">Большие объёмы чёрного металла и производственный лом</div>
                    </div>

                    <div class="rounded-3xl bg-white border border-gray-200 p-4 shadow-sm">
                        <div class="text-xs text-gray-500 mb-2">Тяжёлые конструкции</div>
                        <div class="text-base font-semibold mb-1">Манипулятор</div>
                        <div class="text-sm text-gray-600">Погрузка тяжёлых узлов, станков, балок и металлоконструкций</div>
                    </div>
                </div>
            </div>
        </section>

        <!-- IMPORTANT NOTICE -->
        <section class="mb-5 md:mb-8">
            <div class="rounded-3xl bg-grayBg px-5 py-5 md:px-8 md:py-6 border border-gray-200">
                <h2 class="text-xl md:text-2xl font-semibold mb-3">
                    Бесплатный вывоз — что это значит
                </h2>
                <p class="text-gray-700 leading-relaxed mb-3">
                    Мы всегда организуем выезд транспорта <strong>бесплатно</strong>. Стоимость подачи машины,
                    маршрут и логистика не оплачиваются отдельно. Мы подбираем подходящий транспорт под ваш
                    объём и тип металлолома, а итоговые условия согласовываются менеджером заранее.
                </p>
                <p class="text-sm text-gray-600 leading-relaxed">
                    Для быстрого согласования желательно сразу сообщить: примерный объём, тип металла,
                    адрес, возможность подъезда, наличие погрузки и фото металла.
                </p>
            </div>
        </section>

        <!-- VEHICLE PICKER -->
        <section class="mb-5 md:mb-8">
            <div class="flex items-end justify-between gap-4 mb-4">
                <div>
                    <h2 class="text-xl md:text-2xl font-semibold mb-1">
                        Какой транспорт мы отправляем
                    </h2>
                    <p class="text-sm text-gray-600">
                        Выберите тип машины — ниже покажем, для чего она подходит и что может увезти.
                    </p>
                </div>
            </div>

            <div class="flex flex-wrap gap-2 mb-5">
                <button
                    v-for="vehicle in delivery.vehicles"
                    :key="vehicle.id"
                    type="button"
                    @click="selectVehicle(vehicle.id)"
                    class="rounded-full border px-4 py-2 text-sm font-medium transition"
                    :class="delivery.selectedVehicle === vehicle.id
                        ? 'bg-baseColor text-white border-baseColor shadow-sm'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-baseColor hover:text-baseColor'"
                >
                    {{ vehicle.title }}
                </button>
            </div>

            <div
                class="grid grid-cols-1 lg:grid-cols-[1.05fr_.95fr] gap-5 rounded-3xl border border-gray-200 bg-white p-5 md:p-6 shadow-sm">
                <div>
                    <div class="flex flex-wrap items-center gap-2 mb-3">
                        <div class="text-2xl md:text-3xl font-semibold">
                            {{ delivery.currentVehicle.title }}
                        </div>
                        <div
                            class="inline-flex items-center rounded-full bg-gray-100 border border-gray-200 px-3 py-1 text-sm text-gray-700">
                            {{ delivery.currentVehicle.capacity }}
                        </div>
                        <div
                            v-if="delivery.currentVehicle.freePickup"
                            class="inline-flex items-center rounded-full bg-green-50 border border-green-200 px-3 py-1 text-sm text-green-700">
                            Бесплатный вывоз
                        </div>
                    </div>

                    <p class="text-gray-700 leading-relaxed mb-5">
                        {{ delivery.currentVehicle.description }}
                    </p>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                        <div class="rounded-2xl bg-gray-50 border border-gray-200 p-4">
                            <div class="text-sm text-gray-500 mb-1">Подходит для</div>
                            <div class="font-medium">{{ delivery.currentVehicle.bestFor }}</div>
                        </div>

                        <div class="rounded-2xl bg-gray-50 border border-gray-200 p-4">
                            <div class="text-sm text-gray-500 mb-1">Погрузка</div>
                            <div class="font-medium">{{ delivery.currentVehicle.loading }}</div>
                        </div>

                        <div class="rounded-2xl bg-gray-50 border border-gray-200 p-4">
                            <div class="text-sm text-gray-500 mb-1">Где используется</div>
                            <div class="font-medium">{{ delivery.currentVehicle.useCases }}</div>
                        </div>

                        <div class="rounded-2xl bg-gray-50 border border-gray-200 p-4">
                            <div class="text-sm text-gray-500 mb-1">Ограничения</div>
                            <div class="font-medium">{{ delivery.currentVehicle.restrictions }}</div>
                        </div>
                    </div>

                    <div class="rounded-2xl bg-grayBg border border-gray-200 p-4">
                        <div class="font-semibold mb-3">Что обычно вывозим этой машиной</div>
                        <div class="flex flex-wrap gap-2">
                            <span
                                v-for="item in delivery.currentVehicle.items"
                                :key="item"
                                class="rounded-full bg-white border border-gray-200 px-3 py-1.5 text-sm text-gray-700"
                            >
                                {{ item }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="grid grid-cols-1 gap-3">
                    <div class="rounded-2xl bg-grayBg border border-gray-200 p-4">
                        <div class="font-semibold mb-3">Когда стоит выбрать именно этот вариант</div>
                        <ul class="space-y-2 text-sm text-gray-700">
                            <li
                                v-for="point in delivery.currentVehicle.advantages"
                                :key="point"
                                class="flex items-start gap-2"
                            >
                                <span class="mt-1 w-2 h-2 rounded-full bg-baseColor shrink-0"></span>
                                <span>{{ point }}</span>
                            </li>
                        </ul>
                    </div>

                    <div class="rounded-2xl bg-white border border-gray-200 p-4">
                        <div class="font-semibold mb-3">Что нужно сообщить менеджеру</div>
                        <ul class="space-y-2 text-sm text-gray-700">
                            <li class="flex items-start gap-2">
                                <span class="mt-1 w-2 h-2 rounded-full bg-black shrink-0"></span>
                                <span>Адрес и ориентиры для подъезда</span>
                            </li>
                            <li class="flex items-start gap-2">
                                <span class="mt-1 w-2 h-2 rounded-full bg-black shrink-0"></span>
                                <span>Фото металлолома и примерный объём</span>
                            </li>
                            <li class="flex items-start gap-2">
                                <span class="mt-1 w-2 h-2 rounded-full bg-black shrink-0"></span>
                                <span>Нужна ли погрузка, резка или демонтаж</span>
                            </li>
                            <li class="flex items-start gap-2">
                                <span class="mt-1 w-2 h-2 rounded-full bg-black shrink-0"></span>
                                <span>Есть ли ограничения по высоте, двору, воротам, пропускам</span>
                            </li>
                        </ul>
                    </div>

                    <div class="rounded-2xl bg-green-50 border border-green-200 p-4">
                        <div class="font-semibold text-green-800 mb-2">Важно</div>
                        <p class="text-sm text-green-800 leading-relaxed">
                            Бесплатный вывоз действует всегда. Тип транспорта определяется по объёму,
                            весу, формату лома и условиям на адресе, чтобы вывоз был безопасным
                            и удобным для клиента.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <!-- ALL VEHICLES GRID -->
        <section class="mb-5 md:mb-8">
            <h2 class="text-xl md:text-2xl font-semibold mb-4">Все варианты транспорта</h2>

            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <button
                    v-for="vehicle in delivery.vehicles"
                    :key="vehicle.id + '_card'"
                    type="button"
                    @click="selectVehicle(vehicle.id)"
                    class="text-left rounded-3xl border p-5 transition shadow-sm"
                    :class="delivery.selectedVehicle === vehicle.id
                        ? 'border-baseColor bg-baseColor/5'
                        : 'border-gray-200 bg-white hover:border-baseColor/60'"
                >
                    <div class="flex items-start justify-between gap-3 mb-3">
                        <div>
                            <div class="text-lg font-semibold">{{ vehicle.title }}</div>
                            <div class="text-sm text-gray-500">{{ vehicle.capacity }}</div>
                        </div>
                        <div
                            v-if="vehicle.freePickup"
                            class="rounded-full bg-green-50 border border-green-200 px-2.5 py-1 text-xs font-medium text-green-700">
                            Бесплатно
                        </div>
                    </div>

                    <p class="text-sm text-gray-700 leading-relaxed mb-4">
                        {{ vehicle.shortDescription }}
                    </p>

                    <div class="grid grid-cols-2 gap-2 text-sm">
                        <div class="rounded-xl bg-gray-50 border border-gray-200 p-3">
                            <div class="text-gray-500 mb-1">Для чего</div>
                            <div class="font-medium">{{ vehicle.bestFor }}</div>
                        </div>
                        <div class="rounded-xl bg-gray-50 border border-gray-200 p-3">
                            <div class="text-gray-500 mb-1">Погрузка</div>
                            <div class="font-medium">{{ vehicle.loading }}</div>
                        </div>
                    </div>
                </button>
            </div>
        </section>

        <!-- WHAT WE TAKE -->
        <section class="mb-5 md:mb-8">
            <div class="rounded-3xl bg-grayBg border border-gray-200 px-5 py-6 md:px-8 md:py-8">
                <h2 class="text-xl md:text-2xl font-semibold mb-4">Что можем вывезти</h2>

                <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                    <div
                        v-for="item in delivery.acceptedCategories"
                        :key="item"
                        class="rounded-2xl bg-white border border-gray-200 px-4 py-3 text-sm md:text-base shadow-sm"
                    >
                        {{ item }}
                    </div>
                </div>

                <p class="mt-4 text-sm text-gray-600">
                    Если у вас смешанный лом, не страшно — пришлите фото, мы подскажем, какая машина подойдёт лучше.
                </p>
            </div>
        </section>

        <!-- HOW IT WORKS -->
        <section class="mb-5 md:mb-8">
            <h2 class="text-xl md:text-2xl font-semibold mb-4">Как проходит вывоз</h2>

            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div class="rounded-3xl bg-white border border-gray-200 p-5 shadow-sm">
                    <div class="w-10 h-10 rounded-2xl bg-baseColor text-white flex items-center justify-center font-semibold mb-4">1</div>
                    <div class="font-semibold mb-2">Оставляете заявку</div>
                    <p class="text-sm text-gray-600 leading-relaxed">
                        Сообщаете адрес, тип металла, примерный объём и прикладываете фото.
                    </p>
                </div>

                <div class="rounded-3xl bg-white border border-gray-200 p-5 shadow-sm">
                    <div class="w-10 h-10 rounded-2xl bg-baseColor text-white flex items-center justify-center font-semibold mb-4">2</div>
                    <div class="font-semibold mb-2">Подбираем машину</div>
                    <p class="text-sm text-gray-600 leading-relaxed">
                        Определяем, что лучше подойдёт: газель, ломовоз, манипулятор или контейнер.
                    </p>
                </div>

                <div class="rounded-3xl bg-white border border-gray-200 p-5 shadow-sm">
                    <div class="w-10 h-10 rounded-2xl bg-baseColor text-white flex items-center justify-center font-semibold mb-4">3</div>
                    <div class="font-semibold mb-2">Приезжаем на адрес</div>
                    <p class="text-sm text-gray-600 leading-relaxed">
                        Вывозим лом с объекта, учитывая подъезд, безопасность и формат погрузки.
                    </p>
                </div>

                <div class="rounded-3xl bg-white border border-gray-200 p-5 shadow-sm">
                    <div class="w-10 h-10 rounded-2xl bg-baseColor text-white flex items-center justify-center font-semibold mb-4">4</div>
                    <div class="font-semibold mb-2">Забираем металл</div>
                    <p class="text-sm text-gray-600 leading-relaxed">
                        Вы получаете понятный сервис без отдельной оплаты за подачу транспорта.
                    </p>
                </div>
            </div>
        </section>

        <!-- CONDITIONS -->
        <section class="mb-5 md:mb-8">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div class="rounded-3xl bg-grayBg border border-gray-200 px-5 py-6 md:px-8 md:py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Когда нужен манипулятор или спецтехника</h2>
                    <ul class="space-y-3 text-gray-700">
                        <li class="flex items-start gap-3">
                            <span class="mt-1 w-2.5 h-2.5 rounded-full bg-baseColor shrink-0"></span>
                            <span>Если лом тяжёлый, крупногабаритный или его невозможно загрузить вручную.</span>
                        </li>
                        <li class="flex items-start gap-3">
                            <span class="mt-1 w-2.5 h-2.5 rounded-full bg-baseColor shrink-0"></span>
                            <span>Если речь о балках, швеллерах, металлоконструкциях, станках, трансформаторах, двигателях.</span>
                        </li>
                        <li class="flex items-start gap-3">
                            <span class="mt-1 w-2.5 h-2.5 rounded-full bg-baseColor shrink-0"></span>
                            <span>Если на объекте нужен быстрый вывоз большого объёма за один рейс.</span>
                        </li>
                        <li class="flex items-start gap-3">
                            <span class="mt-1 w-2.5 h-2.5 rounded-full bg-baseColor shrink-0"></span>
                            <span>Если требуется установка контейнера под накопление лома.</span>
                        </li>
                    </ul>
                </div>

                <div class="rounded-3xl bg-grayBg border border-gray-200 px-5 py-6 md:px-8 md:py-8">
                    <h2 class="text-xl md:text-2xl font-semibold mb-4">Что важно перед выездом</h2>
                    <ul class="space-y-3 text-gray-700">
                        <li class="flex items-start gap-3">
                            <span class="mt-1 w-2.5 h-2.5 rounded-full bg-black shrink-0"></span>
                            <span>Нужно понимать, есть ли свободный подъезд для машины.</span>
                        </li>
                        <li class="flex items-start gap-3">
                            <span class="mt-1 w-2.5 h-2.5 rounded-full bg-black shrink-0"></span>
                            <span>Желательно заранее сообщить, требуется ли погрузка, резка или демонтаж.</span>
                        </li>
                        <li class="flex items-start gap-3">
                            <span class="mt-1 w-2.5 h-2.5 rounded-full bg-black shrink-0"></span>
                            <span>Для закрытых территорий лучше заранее подготовить пропуск или контакты охраны.</span>
                        </li>
                        <li class="flex items-start gap-3">
                            <span class="mt-1 w-2.5 h-2.5 rounded-full bg-black shrink-0"></span>
                            <span>Если объём большой, фотографии сильно ускоряют согласование транспорта.</span>
                        </li>
                    </ul>
                </div>
            </div>
        </section>

        <!-- FAQ -->
        <section class="mb-8 md:mb-12">
            <div class="rounded-3xl bg-white border border-gray-200 px-5 py-6 md:px-8 md:py-8 shadow-sm">
                <h2 class="text-xl md:text-2xl font-semibold mb-5">Частые вопросы</h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div
                        v-for="faq in delivery.faq"
                        :key="faq.question"
                        class="rounded-2xl bg-grayBg border border-gray-200 p-4"
                    >
                        <div class="font-semibold mb-2">{{ faq.question }}</div>
                        <p class="text-sm text-gray-700 leading-relaxed">{{ faq.answer }}</p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<style scoped>
</style>
